import { RecommendedArticlesType } from "@bundles/parts/components/PureList/ContextProviders/Particular/ParticularContext"
import { getParameterFromUserContextModule, useUser, useWorkTask } from "@tm/context-distribution"
import { Article, ArticleListType, channel, CrossSellingModalType, ECrossSellingType, UserModuleType } from "@tm/models"
import { useCallback, useMemo } from "react"
import { useRecoilState } from "recoil"
import { CrossSellingModalToOpen, CrossSellingProductGroup } from "@tm/data/articles"
import { TmaHelper } from "@tm/utils"
import { CrossSellingCombinedStates, crossSellingListId, getCrossSellingDefaultState } from "../crossSellingStates"
import { emptyFunc } from "../../../helpers"
import { createUniversalSearchRequestPayload, createVehiclePartsRequestPayload } from "../helpers"
import { useCrossSellingActions } from "./useCrossSellingActions"
import { CrossSellingContext } from "../models"

export function useCrossSelling(listType: ArticleListType, isAlternativeSearch?: boolean, crossSellingModalType?: CrossSellingModalType) {
    const workTask = useWorkTask()!
    const vehicle = workTask.workTask?.vehicle
    const workTaskId = workTask.workTaskId!

    const { userContext } = useUser()
    const context = getParameterFromUserContextModule(userContext, UserModuleType.CrossSelling, "Context")

    const crossSellingActions = useCrossSellingActions(listType, isAlternativeSearch, crossSellingModalType)

    const [{ isLoading, referenceArticle, chains, data, preventReset }, setCrossSellingData] = useRecoilState(CrossSellingCombinedStates(workTaskId))

    const displayCustomerAlsoBoughtButton = useMemo(() => {
        switch (context) {
            case CrossSellingContext[ECrossSellingType.alsoBoughtArticles]:
                return !(data.alsoBoughtArticles.length > 0)

            case CrossSellingContext[ECrossSellingType.alsoBoughtProductGroups]:
                return true
            default:
                return false
        }
    }, [data.alsoBoughtArticles?.length, context])

    const isEnabled = useMemo(
        () =>
            referenceArticle &&
            (!!data.recommendedProductGroups?.length || !!data.alsoBoughtProductGroups?.length || !!data.alsoBoughtArticles?.length),
        [data.alsoBoughtArticles?.length, data.alsoBoughtProductGroups?.length, data.recommendedProductGroups?.length, referenceArticle]
    )

    const displayCustomerAlsoBoughtTab = useMemo(
        () => !!data.alsoBoughtArticles?.length || !!data.alsoBoughtProductGroups?.length,
        [data.alsoBoughtArticles?.length, data.alsoBoughtProductGroups?.length]
    )

    const onClose = useCallback(() => {
        crossSellingActions?.reset()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onReset = useCallback(() => {
        setCrossSellingData((prevState) => {
            if (prevState.preventReset) {
                return prevState
            }

            return getCrossSellingDefaultState()
        })
    }, [setCrossSellingData])

    const setPreventReset = useCallback(
        (newResetState: boolean) => {
            setCrossSellingData((prevState) => ({
                ...prevState,
                preventReset: newResetState,
            }))
        },
        [setCrossSellingData]
    )

    const onProductGroupClick = useCallback(
        (productGroup: CrossSellingProductGroup, crossSellingModalType: CrossSellingModalType) => {
            if (!productGroup) {
                return
            }

            if (isAlternativeSearch) {
                setPreventReset(true)
            }

            switch (productGroup.articleListToOpen) {
                case CrossSellingModalToOpen.PKW:
                    TmaHelper.ArticleListFiltered.ArticleListFiltered.Search.SubmitCrossSelling(crossSellingModalType, "")

                    channel("WORKTASK").publish(
                        "PARTS/REQUEST_LIST",
                        createVehiclePartsRequestPayload(vehicle?.tecDocTypeId, [productGroup.id], crossSellingModalType)
                    )

                    break

                default:
                    TmaHelper.UniParts.Search.SubmitCrossSelling(crossSellingModalType, productGroup.name)

                    channel("WORKTASK").publish("PARTS/REQUEST_LIST", createUniversalSearchRequestPayload(productGroup.name, crossSellingModalType))

                    break
            }
        },
        [isAlternativeSearch, setCrossSellingData, vehicle?.tecDocTypeId]
    )

    const pureListContext: RecommendedArticlesType = useMemo(() => {
        return {
            type: crossSellingListId,
            articles: data.alsoBoughtArticles,
            request: data.alsoBoughtArticles
                ?.map((x) => `${x.id ?? ""}_${x.internalId ?? 0}_${x.traderArticleNo ?? ""}_${x.supplierArticleNo ?? ""}_${x.quantity}`)
                ?.join(","),
            toggleSelectedArticle: emptyFunc,
            onAddToBasket: (article: Article, quantity: number) => {
                crossSellingActions.loadNextRecommendation([article])
            },
            onDelete: () => {
                crossSellingActions.reset()
            },
        }
    }, [data.alsoBoughtArticles, crossSellingActions])

    return useMemo(
        () => ({
            pureListContext,
            isLoading,
            isEnabled,
            data,
            chains,
            preventReset,
            onProductGroupClick,
            displayCustomerAlsoBoughtTab,
            onClose,
            onReset,
            displayCustomerAlsoBoughtButton,
            setPreventReset,
            referenceArticle,
        }),
        [
            pureListContext,
            isLoading,
            isEnabled,
            data,
            chains,
            preventReset,
            onProductGroupClick,
            displayCustomerAlsoBoughtTab,
            onClose,
            onReset,
            setPreventReset,
            displayCustomerAlsoBoughtButton,
            referenceArticle,
        ]
    )
}
